import React from 'react';
import PropTypes from 'prop-types';
import { Formik, yupToFormErrors } from 'formik';
import * as Yup from 'yup';

import { getTranslatedString } from 'helpers/i18n';

const requiredErrorVerbiage = getTranslatedString('required');

const validationSchema = {
  internal_name: Yup.string().required(requiredErrorVerbiage),
  internal_description: Yup.string().required(requiredErrorVerbiage),
  external_name: Yup.string()
    .nullable()
    .when('externally_visible', {
      is: true,
      then: (schema) => schema.required(requiredErrorVerbiage),
      otherwise: (schema) => schema,
    }),
  external_description: Yup.string()
    .nullable()
    .when('externally_visible', {
      is: true,
      then: (schema) => schema.required(requiredErrorVerbiage),
      otherwise: (schema) => schema,
    }),
  schedule_monthly_duration_months: Yup.number()
    .nullable()
    .when('schedule_type', {
      is: 'monthly',
      then: (schema) => schema.required(requiredErrorVerbiage),
      otherwise: (schema) => schema,
    }),
  schedule_monthly_starting_month: Yup.number()
    .nullable()
    .when('schedule_type', {
      is: 'monthly',
      then: (schema) => schema.required(requiredErrorVerbiage),
      otherwise: (schema) => schema,
    }),
  schedule_custom_events: Yup.array().when('schedule_type', {
    is: 'custom',
    then: (schema) => schema.min(1, requiredErrorVerbiage),
    otherwise: (schema) => schema,
  }),
  incentives: Yup.array().min(1, requiredErrorVerbiage),
  incentive_codes: Yup.string().required(requiredErrorVerbiage),
  internal_code: Yup.string().required(requiredErrorVerbiage),
};

const validateForm = async (values) => {
  const schema = Yup.object().shape(validationSchema);

  // Yup automatically cast strings to numbers, and in this case were casting empty strings as NaN.
  // We catch those cases and set them as undefined instead of empty strings to avoid the wrong casting.
  if (values.schedule_monthly_amount === '') {
    values.schedule_monthly_amount = undefined;
  }

  if (values.schedule_monthly_duration_months === '') {
    values.schedule_monthly_duration_months = undefined;
  }

  return schema.validate(values, { abortEarly: false }).catch((error) => {
    throw yupToFormErrors(error);
  });
};

const defaultValues = {
  id: undefined,
  status: 'draft',
  internal_name: undefined,
  internal_description: undefined,
  externally_visible: false,
  external_name: undefined,
  external_description: undefined,
  incentive_type: 'rental_discount',
  target_fee: 'application_fee',
  deferral_until_month: 1,
  approval_type: 'automatic',
  incentive_codes: undefined,
  internal_code: undefined,
  schedule_type: 'monthly',
  calculation_type: 'percentage',
  schedule_monthly_amount: undefined,
  schedule_monthly_duration_months: undefined,
  schedule_monthly_starting_month: 1,
  schedule_custom_events: [],
  incentives: [],
};

const IncentiveTemplateForm = ({ formData, handleSubmit, children }) => {
  const initialValues = {
    ...defaultValues,
    ...formData,
  };

  return (
    <Formik initialValues={initialValues} validate={validateForm} enableReinitialize>
      {children}
    </Formik>
  );
};

IncentiveTemplateForm.propTypes = {
  formData: PropTypes.object.isRequired,
};

export default IncentiveTemplateForm;
